import { useEffect, useState } from 'react';
import { useRuntimeConfig } from '../lib/context';

interface NetworkLog {
  timestamp: number;
  url: string;
  method: string;
  status?: number;
  error?: string;
  traceId?: string;
}

interface HudState {
  initDataLength?: number;
  verifyStatus?: 'pending' | 'success' | 'error';
  verifyCode?: number | string;
  lastError?: string;
  traceId?: string;
  networkLogs: NetworkLog[];
}

export function DebugHUD({ enabled }: { enabled: boolean }) {
  const config = useRuntimeConfig();
  const [state, setState] = useState<HudState>({ networkLogs: [] });

  useEffect(() => {
    if (!enabled) {
      return;
    }

    const handleVerify = (event: Event) => {
      const detail = (event as CustomEvent).detail ?? {};
      setState(prev => ({
        ...prev,
        verifyStatus: detail.status,
        verifyCode: detail.code ?? prev.verifyCode,
        lastError: detail.error ?? prev.lastError,
        traceId: detail.traceId ?? prev.traceId,
      }));
    };

    const handleNetwork = (event: Event) => {
      const detail = (event as CustomEvent).detail ?? {};
      const log: NetworkLog = {
        timestamp: Date.now(),
        url: detail.url,
        method: detail.method,
        status: detail.status,
        error: detail.error,
        traceId: detail.traceId,
      };
      setState(prev => ({
        ...prev,
        networkLogs: [...prev.networkLogs.slice(-9), log],
      }));
    };

    const handleInitData = (event: Event) => {
      const detail = (event as CustomEvent).detail ?? {};
      setState(prev => ({ ...prev, initDataLength: detail.length }));
    };

    window.addEventListener('debughud:verify', handleVerify as EventListener);
    window.addEventListener('webapp:network', handleNetwork as EventListener);
    window.addEventListener('webapp:initdata', handleInitData as EventListener);

    return () => {
      window.removeEventListener('debughud:verify', handleVerify as EventListener);
      window.removeEventListener('webapp:network', handleNetwork as EventListener);
      window.removeEventListener('webapp:initdata', handleInitData as EventListener);
    };
  }, [enabled]);

  if (!enabled) {
    return null;
  }

  return (
    <div className="hud">
      <div className="hud__header">Lexy MiniApp Debug</div>
      <div className="hud__row">
        <span>Build:</span>
        <span>{config.buildVersion}</span>
      </div>
      {state.traceId && (
        <div className="hud__row">
          <span>Trace:</span>
          <span>{state.traceId}</span>
        </div>
      )}
      {state.verifyStatus && (
        <div className="hud__row">
          <span>Verify:</span>
          <span>
            {state.verifyStatus === 'pending' && '⏳ pending'}
            {state.verifyStatus === 'success' && `✅ ${state.verifyCode ?? 200}`}
            {state.verifyStatus === 'error' && `❌ ${state.verifyCode ?? 'error'}`}
          </span>
        </div>
      )}
      {state.initDataLength !== undefined && (
        <div className="hud__row">
          <span>initData:</span>
          <span>{state.initDataLength} chars</span>
        </div>
      )}
      {state.lastError && (
        <div className="hud__row hud__row--error">
          <span>Error:</span>
          <span>{state.lastError}</span>
        </div>
      )}
      {state.networkLogs.length > 0 && (
        <div className="hud__logs">
          <div className="hud__logs-title">Network</div>
          {state.networkLogs.map(log => (
            <div key={log.timestamp} className="hud__log-line">
              <span>{new Date(log.timestamp).toLocaleTimeString()}</span>
              <span>{log.method}</span>
              <span>{log.status ?? '...'}</span>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
